import React, { useEffect, useState, useRef } from 'react'
import { runStatsigSessionReplay } from '@statsig/session-replay'
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings'
import { getEnvironment } from '../Common'
import { ensureStatsigUserFromSsoSession } from './statsigMethod'
import { STATSIG_CLIENT_KEY } from './statsigKey'
import { bindStatsigClient, STATSIG_USER_INFO_KEY } from './logEvent'

const StatsigWrapper = React.memo(({ children, waitForInit = false }) => {
    const initRef = useRef(false)
    const [client, setClient] = useState(null)

    const { client: statsigClient } = useClientAsyncInit(
        STATSIG_CLIENT_KEY,
        {
            userAgent: navigator.userAgent,
            language: navigator.language,
            custom: {
                origin: window.location.origin || '',
            },
        },
        {
            environment: {
                tier: getEnvironment(),
            },
        }
    )

    useEffect(() => {
        if (!statsigClient || initRef.current) return

        const initializeStatsig = async () => {
            if (statsigClient.loadingStatus === 'Ready' && !initRef.current) {
                try {
                    console.log('Statsig initializing...')
                    initRef.current = true

                    const context = statsigClient.getContext()
                    const derivedFields = context?.values?.derived_fields

                    await ensureStatsigUserFromSsoSession(statsigClient)

                    const savedRaw = sessionStorage.getItem(STATSIG_USER_INFO_KEY)
                    let parsedUser = null
                    try {
                        if (savedRaw) parsedUser = JSON.parse(savedRaw)
                    } catch (e) {
                        parsedUser = null
                    }
                    const statsigReady =
                        parsedUser?.email &&
                        parsedUser?.custom?.customerid

                    if (statsigReady) {
                        await statsigClient.updateUserAsync({
                            ...parsedUser,
                            customIDs: {
                                ...(parsedUser?.customIDs || {}),
                                IP: derivedFields?.ip || '',
                            },
                        })
                    } else {
                        await statsigClient.updateUserAsync({
                            customIDs: {
                                IP: derivedFields?.ip || '',
                            },
                        })
                    }

                    runStatsigSessionReplay(statsigClient)
                    bindStatsigClient(statsigClient)

                    setClient(statsigClient)
                    console.log('Statsig initialized successfully')
                } catch (error) {
                    console.error('Error initializing Statsig:', error)
                    initRef.current = false
                }
            }
        }

        initializeStatsig()
    }, [statsigClient?.loadingStatus])

    if (waitForInit) {
        if (!client) {
            return null
        }
        return <StatsigProvider client={client}>{children}</StatsigProvider>
    }else {
        return client ? (
            <StatsigProvider client={client}>{children}</StatsigProvider>
        ) : (
            children
        )
    }

})

StatsigWrapper.displayName = 'StatsigWrapper'

export default StatsigWrapper
